import {
  ConsentChannelEnum,
  type ConsentInput,
  ConsentPurposeEnum,
} from "src/graphql-next/generated/types";
import { z } from "zod";

export function createChannelSelection(options: {
  isAccepted: boolean;
}): ChannelSelection {
  return {
    email: options.isAccepted,
    sms: options.isAccepted,
    whatsapp: options.isAccepted,
    phone: options.isAccepted,
  };
}

export function createConsentPreferences(options: {
  isAccepted: boolean;
}): ConsentPreferences {
  return {
    personalizedOffers: createChannelSelection(options),
    partnerOffers: createChannelSelection(options),
    propertyNews: createChannelSelection(options),
    events: createChannelSelection(options),
    promotions: createChannelSelection(options),
    propertyAlerts: createChannelSelection(options),
    satisfactionSurveys: createChannelSelection(options),
  };
}

export function hasSelectedChannel(channels: ChannelSelection): boolean {
  return Object.values(channels).some(Boolean);
}

export function countActivePurposes(preferences: ConsentPreferences): number {
  return Object.values(preferences).filter(hasSelectedChannel).length;
}

export function hasAcceptedEverything(
  preferences: ConsentPreferences,
): boolean {
  return Object.values(preferences).every((channels) =>
    Object.values(channels).every(Boolean),
  );
}

export function setChannelSelection(
  selection: ChannelSelection,
  options: {
    channel: keyof ChannelSelection;
    isAccepted: boolean;
  },
): ChannelSelection {
  return { ...selection, [options.channel]: options.isAccepted };
}

export function toConsentInputs(
  preferences: ConsentPreferences,
): ConsentInput[] {
  return (
    Object.keys(PURPOSE_BY_PREFERENCE) as Array<keyof ConsentPreferences>
  ).map((preference) => ({
    purpose: PURPOSE_BY_PREFERENCE[preference],
    channels: (
      Object.keys(CHANNEL_BY_SELECTION) as Array<keyof ChannelSelection>
    )
      .filter((channel) => preferences[preference][channel])
      .map((channel) => CHANNEL_BY_SELECTION[channel]),
  }));
}

export const channelSelectionSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
  whatsapp: z.boolean(),
  phone: z.boolean(),
});
export const consentPreferencesSchema = z.object({
  personalizedOffers: channelSelectionSchema,
  partnerOffers: channelSelectionSchema,
  propertyNews: channelSelectionSchema,
  events: channelSelectionSchema,
  promotions: channelSelectionSchema,
  propertyAlerts: channelSelectionSchema,
  satisfactionSurveys: channelSelectionSchema,
});
export type ChannelSelection = z.infer<typeof channelSelectionSchema>;
export type ConsentPreferences = z.infer<typeof consentPreferencesSchema>;

const PURPOSE_BY_PREFERENCE: Record<
  keyof ConsentPreferences,
  ConsentPurposeEnum
> = {
  personalizedOffers: ConsentPurposeEnum.PersonalizedOffers,
  partnerOffers: ConsentPurposeEnum.PartnerOffers,
  propertyNews: ConsentPurposeEnum.RealEstateNews,
  events: ConsentPurposeEnum.EventsAndWebinars,
  promotions: ConsentPurposeEnum.ContestsAndPromotions,
  propertyAlerts: ConsentPurposeEnum.RealEstateAlerts,
  satisfactionSurveys: ConsentPurposeEnum.SatisfactionSurvey,
};

const CHANNEL_BY_SELECTION: Record<keyof ChannelSelection, ConsentChannelEnum> =
  {
    email: ConsentChannelEnum.Email,
    sms: ConsentChannelEnum.Sms,
    whatsapp: ConsentChannelEnum.Whatsapp,
    phone: ConsentChannelEnum.Phone,
  };
